import { FormBuilder } from '@/features/form_builder';
import {
  SubmissionTemplateVersionData,
  useSubmissionTemplateVersion,
} from '@/hooks/submission_forms/useSubmissionTemplateVersion';
import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface ReadOnlyEditorPanelProps {
  templateId: string;
}

// "Editor" tab while manual template editing is turned off: templates are
// managed in code, and every write endpoint answers 403. Shows the live
// (published) version in a read-only FormBuilder. Deliberately separate from
// EditorPanel, which opens a draft (a POST) the moment it mounts.
const ReadOnlyEditorPanel: React.FC<ReadOnlyEditorPanelProps> = ({
  templateId,
}) => {
  const { listVersions, getVersion } = useSubmissionTemplateVersion();

  const [version, setVersion] = useState<SubmissionTemplateVersionData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const versions = await listVersions(templateId);
        const published = versions.find(v => v.status === 'published');
        const full = published
          ? await getVersion(templateId, published.id)
          : null;
        if (!cancelled) setVersion(full);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load form');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [templateId, listVersions, getVersion]);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-gray-600">Loading form...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-gray-600">
        This template has no published version yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)]">
      <div className="bg-white border-b px-5 py-2.5 shrink-0 text-xs text-gray-500">
        Viewing published{' '}
        <span className="font-mono">v{version.version_number}</span>
      </div>
      <div className="flex-1 min-h-0">
        <FormBuilder
          key={version.id}
          initialSections={version.question_tree || []}
          readOnly
        />
      </div>
    </div>
  );
};

export default ReadOnlyEditorPanel;
